import loadCalendarEvent from '../../services/loadCalendarEvent';
import CalendarRollUpViewState from '../../store/schema/CalendarRollUpViewState';
import ItemPartViewState from '../../store/schema/ItemPartViewState';
import readingPaneStore from '../../store/Store';
import isNewestOnBottom from '../../utils/isNewestOnBottom';
import { ObservableMap } from 'mobx';
import { ClientItem, mailStore } from 'owa-mail-store';
import ConversationItemParts from 'owa-mail-store/lib/store/schema/ConversationItemParts';
import { getItemToShowFromNodeId } from 'owa-mail-store/lib/utils/conversationsUtils';
import { isMeetingRequest } from 'owa-meeting-message';
import MeetingMessage from 'owa-service/lib/contract/MeetingMessage';
import Message from 'owa-service/lib/contract/Message';
import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import { action, select } from 'satcheljs/lib/legacy';
import {
    ACCEPTED_ITEM_CLASS,
    DECLINED_ITEM_CLASS,
    TENTATIVE_ITEM_CLASS,
} from '../../utils/rollUp/calendarRollUpUtils';
import CalendarItem from 'owa-service/lib/contract/CalendarItem';

export interface GenerateSummaryForCalendarRollUpState {
    loadedConversation: ConversationItemParts;
    itemPartsMap: ObservableMap<string, ItemPartViewState>;
    items: ObservableMap<string, ClientItem>;
}

export default select<GenerateSummaryForCalendarRollUpState>({
    loadedConversation: () =>
        mailStore.conversations.get(
            readingPaneStore.conversationReadingPaneViewState.conversationId.Id
        ),
    itemPartsMap: () => readingPaneStore.conversationReadingPaneViewState.itemPartsMap,
    items: () => mailStore.items,
})(
    action('generateSummaryForCalendarRollUp')(async function generateSummaryForCalendarRollUp(
        state?: GenerateSummaryForCalendarRollUpState
    ) {
        const { conversationNodeIds } = state.loadedConversation;

        // There is no need to generate summary if there is only one node in the conversation.
        if (conversationNodeIds.length <= 1) {
            return;
        }

        // Starts from the newest message to generate the summary
        const startIndex = isNewestOnBottom() ? conversationNodeIds.length - 1 : 0;
        const indexIncrement = isNewestOnBottom() ? -1 : 1;

        // This is used to track that whether the last meeting request message is processed.
        // If there are several meeting requests in the conversation(meeting updates),
        // these meeting requests have the same AssociatedCalendarItemId,
        // so we only load calendar data for the last meeting request if needed.
        // For other meeting requests, we use the local data to generate the summary.
        let hasProcessedLastMeetingRequest = false;

        for (
            let index = startIndex;
            index <= conversationNodeIds.length - 1 && index >= 0;
            index += indexIncrement
        ) {
            const nodeId = conversationNodeIds[index];

            // If a node is deleted and user's setting is hide deleted item, the node is
            // still in conversation node list, but it's not in itemPartsMap.
            if (!state.itemPartsMap.has(nodeId)) {
                continue;
            }

            const itemPartViewState = state.itemPartsMap.get(nodeId);
            const { calendarRollUpViewState } = itemPartViewState;
            const item = state.items.get(itemPartViewState.itemId);

            // If there are response node ids for this node, try to generate the attendee summary
            if (calendarRollUpViewState.calendarReponseNodeIds.length >= 1) {
                const meetingMessage = item as MeetingMessage;
                const calendarEvent =
                    !hasProcessedLastMeetingRequest &&
                    meetingMessage &&
                    meetingMessage.AssociatedCalendarItemId
                        ? await loadCalendarEvent(meetingMessage.AssociatedCalendarItemId)
                        : null;

                initialAttendeeList(calendarRollUpViewState);
                if (calendarEvent) {
                    generateSummaryBasedOnCalendarData(calendarRollUpViewState, calendarEvent);
                }

                // If we fail to get the attendees status from Calendar API,
                // or the attendee status in the meeting response are all not responsed,
                // we fallback to use the information in the current response items
                // to calculate the attendee status.
                if (getAttendeeCountFromSummary(calendarRollUpViewState) == 0) {
                    generateSummaryBasedOnResponseData(calendarRollUpViewState);
                }
            }

            calendarRollUpViewState.isLoading = false;

            if (!hasProcessedLastMeetingRequest && item && isMeetingRequest(item.ItemClass)) {
                hasProcessedLastMeetingRequest = true;
            }
        }
    })
);

function getAttendeeCountFromSummary(calendarRollUpViewState: CalendarRollUpViewState): number {
    const { acceptedAttendees, tentativeAttendees, declinedAttendees } = calendarRollUpViewState;
    return acceptedAttendees.length + tentativeAttendees.length + declinedAttendees.length;
}

function initialAttendeeList(calendarRollUpViewState: CalendarRollUpViewState) {
    calendarRollUpViewState.acceptedAttendees = [];
    calendarRollUpViewState.tentativeAttendees = [];
    calendarRollUpViewState.declinedAttendees = [];
    calendarRollUpViewState.proposedNewTimeAttendeesCount = 0;
}

function generateSummaryBasedOnCalendarData(
    calendarRollUpViewState: CalendarRollUpViewState,
    calendarEvent: CalendarItem
) {
    [...calendarEvent.RequiredAttendees, ...calendarEvent.OptionalAttendees].forEach(attendee => {
        let attendeeList = [];
        switch (attendee.ResponseType) {
            case 'Accept':
                attendeeList = calendarRollUpViewState.acceptedAttendees;
                break;
            case 'Tentative':
                attendeeList = calendarRollUpViewState.tentativeAttendees;
                break;
            case 'Decline':
                attendeeList = calendarRollUpViewState.declinedAttendees;
                break;
            default:
                break;
        }
        attendeeList.push({
            Mailbox: attendee.Mailbox,
        });

        // Calculate the count of propose new time
        if (attendee.ProposedStart && attendee.ProposedEnd) {
            calendarRollUpViewState.proposedNewTimeAttendeesCount++;
        }
    });
}

function generateSummaryBasedOnResponseData(calendarRollUpViewState: CalendarRollUpViewState) {
    const attendeeStatus: { [key: string]: Message } = {};
    const {
        calendarReponseNodeIds,
        acceptedAttendees,
        tentativeAttendees,
        declinedAttendees,
    } = calendarRollUpViewState;
    calendarReponseNodeIds.forEach(nodeId => {
        const message: Message = getItemToShowFromNodeId(nodeId);
        if (message) {
            const fromMailbox = message.From ? message.From : message.Sender;
            const fromEmailAddress =
                fromMailbox?.Mailbox?.EmailAddress &&
                fromMailbox.Mailbox.EmailAddress.toLowerCase();
            if (fromEmailAddress) {
                if (
                    !attendeeStatus[fromEmailAddress] ||
                    Date.parse(message.DateTimeSent) >
                        Date.parse(attendeeStatus[fromEmailAddress].DateTimeSent)
                ) {
                    attendeeStatus[fromEmailAddress] = message;
                }
            }
        }
    });

    Object.keys(attendeeStatus).forEach(key => {
        const message = attendeeStatus[key];
        const itemClass = message.ItemClass;
        const attendee: SingleRecipientType = message.From ? message.From : message.Sender;
        if (itemClass.match(ACCEPTED_ITEM_CLASS)) {
            acceptedAttendees.push(attendee);
        } else if (itemClass.match(TENTATIVE_ITEM_CLASS)) {
            tentativeAttendees.push(attendee);
        } else if (itemClass.match(DECLINED_ITEM_CLASS)) {
            declinedAttendees.push(attendee);
        }
    });
}
